import { useState, useRef, useEffect, useCallback } from "react";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import {
  useInternalChat,
  type ChatMessage,
} from "@/features/leads/api/useInternalChat";
import { toast } from "sonner";
import { afterPaint, flashMessage } from "@/features/chat/utils/jumpToMessage";
import { useInternalChatMutations } from "@/features/leads/api/useInternalChatMutations";
import { useMentionableUsers } from "@/features/chat/api/useMentionableUsers";
import { useAppSelector } from "@/stores/hook";
import { ChatHeader } from "./components/ChatHeader";
import { ChatMessageList } from "./components/ChatMessageList";
import ActionForChat from "../Actions/ActionForChat";
import ConfirmDialog from "@/components/ui/ConfirmDialog";

export default function Chat() {
  const { lead } = useLeadForm();
  const { allChildren } = useChildForm();
  const { currentUser } = useAppSelector((state) => state.auth);
  const userId = currentUser?.user?.id;

  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ChatMessage | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const leadId = lead?.id;
  const { messages, isLoading, hasMore, fetchOlder, ensureMessageLoaded } =
    useInternalChat(leadId || null, selectedChildId);

  // JUMP TO A MESSAGE (reply-preview click): page older messages in until
  // the quoted one exists, scroll to it and flash it. The stick-to-bottom
  // effect below must stay out of the way while pages are prepended.
  const suppressAutoScrollRef = useRef(false);
  const jumpToMessage = useCallback(
    async (messageId: string) => {
      suppressAutoScrollRef.current = true;
      try {
        const loaded = await ensureMessageLoaded(messageId);
        await afterPaint();
        if (!loaded || !flashMessage(scrollRef.current, messageId)) {
          toast.info("That message is no longer available");
        }
      } finally {
        suppressAutoScrollRef.current = false;
      }
    },
    [ensureMessageLoaded],
  );
  const { sendMessage, uploadFile, markAsRead, deleteMessage, editMessage } =
    useInternalChatMutations();
  // Authoritative mention roster (creator + assignees + deal users + admins).
  // Everyone who can see this lead (deal chat: the deal) — not just its
  // holders — so SH / QC / ROH people can be tagged (server decides).
  const { users: chatMembers } = useMentionableUsers(leadId || null, selectedChildId);

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMessage.mutate(
      { messageId: pendingDelete.id },
      { onSettled: () => setPendingDelete(null) }
    );
  };

  const children = allChildren || lead?.child || [];
  const selectedLabel = selectedChildId
    ? children.find((c: any) => c.id === selectedChildId)?.name || "Child Chat"
    : "All Chats";

  // Users for @mention. The mentionable endpoint is the source of truth; the
  // local lead object is only a fallback while it loads. NOTE the drawer's
  // lead exposes assignments as `leadUsers` (camel) — the old code read
  // `LeadUsers` (the raw list shape) and so the mention list was EMPTY here.
  const leadUsers: { id: number; name: string; role: string }[] = (() => {
    const usersMap = new Map<
      number,
      { id: number; name: string; role: string }
    >();

    for (const m of chatMembers) {
      usersMap.set(m.id, { id: m.id, name: m.name, role: m.role });
    }

    type AssignRow = {
      isRevoked?: boolean;
      isLeadOwner?: boolean;
      userId?: number;
      user?: { id?: number; name?: string } | null;
    };
    const leadAny = lead as { leadUsers?: AssignRow[]; LeadUsers?: AssignRow[] } | null;
    const rows: AssignRow[] = leadAny?.leadUsers ?? leadAny?.LeadUsers ?? [];
    rows.forEach((lu) => {
      if (lu?.isRevoked) return;
      const uid = lu.user?.id || lu.userId;
      if (uid && !usersMap.has(uid))
        usersMap.set(uid, {
          id: uid,
          name: lu.user?.name || "Unknown",
          role: lu.isLeadOwner ? "Owner" : "Assigned",
        });
    });

    if (lead?.assignedUser?.id && !usersMap.has(lead.assignedUser.id)) {
      usersMap.set(lead.assignedUser.id, {
        id: lead.assignedUser.id,
        name: lead.assignedUser.name,
        role: "Assigned",
      });
    }

    type ChildRow = { name?: string; childUsers?: AssignRow[] };
    ((children || []) as ChildRow[]).forEach((c) => {
      (c.childUsers || []).forEach((cu) => {
        if (cu?.isRevoked) return;
        const uid = cu.user?.id || cu.userId;
        if (uid && !usersMap.has(uid))
          usersMap.set(uid, {
            id: uid,
            name: cu.user?.name || "Unknown",
            role: c.name || "Deal",
          });
      });
    });

    return [...usersMap.values()];
  })();

  useEffect(() => {
    if (suppressAutoScrollRef.current) return;
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    if (!leadId || !userId || messages.length === 0) return;
    const unread = messages
      .filter(
        (m) =>
          m.senderId !== userId && !m.readBy.some((r) => r.userId === userId)
      )
      .map((m) => m.id);
    if (unread.length > 0)
      markAsRead.mutate({
        leadId,
        childId: selectedChildId,
        messageIds: unread,
      });
  }, [messages, leadId, userId, selectedChildId]);

  const handleSend = (
    text: string,
    mentionsList?: { userId: number; name: string }[]
  ) => {
    if (!leadId || !text.trim()) return;
    sendMessage.mutate({
      leadId,
      childId: selectedChildId,
      body: text.trim(),
      mentions:
        mentionsList && mentionsList.length > 0 ? mentionsList : undefined,
      replyToId: replyTo?.id,
    });
    setReplyTo(null);
  };

  const handleSaveEdit = (
    messageId: string,
    text: string,
    mentionsList?: { userId: number; name: string }[]
  ) => {
    editMessage.mutate({ messageId, body: text, mentions: mentionsList });
    setEditing(null);
  };

  // Edit and reply are exclusive — starting one drops the other.
  const startEdit = (msg: ChatMessage) => {
    setReplyTo(null);
    setEditing(msg);
  };
  const startReply = (msg: ChatMessage) => {
    setEditing(null);
    setReplyTo(msg);
  };

  // Switching channel abandons an in-progress edit/reply.
  useEffect(() => {
    setEditing(null);
    setReplyTo(null);
  }, [selectedChildId]);

  const handleFileUpload = async (file: File) => {
    if (!leadId) return;
    const media = await uploadFile.mutateAsync({
      leadId,
      childId: selectedChildId,
      file,
    });
    const mimePrefix = (media.mediaMime || "").split("/")[0];
    sendMessage.mutate({
      leadId,
      childId: selectedChildId,
      type:
        mimePrefix === "image"
          ? "image"
          : mimePrefix === "audio"
            ? "audio"
            : "document",
      mediaUrl: media.mediaUrl,
      mediaMime: media.mediaMime,
      mediaName: media.mediaName,
      mediaSize: media.mediaSize,
      replyToId: replyTo?.id,
    });
    setReplyTo(null);
  };

  const handleVoiceNote = async (blob: Blob) => {
    if (!leadId) return;
    const media = await uploadFile.mutateAsync({
      leadId,
      childId: selectedChildId,
      file: blob as any,
    });
    sendMessage.mutate({
      leadId,
      childId: selectedChildId,
      type: "audio",
      body: "Voice note",
      mediaUrl: media.mediaUrl,
      mediaMime: media.mediaMime,
      mediaName: "Voice note",
    });
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <div className="shrink-0">
        <ChatHeader
          selectedLabel={selectedLabel}
          selectedChildId={selectedChildId}
          children={children}
          onSelectChat={setSelectedChildId}
        />
      </div>

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#f7f9f8]"
        style={{ scrollbarWidth: "thin" }}
      >
        {/*
          `mt-auto` keeps short message lists pinned to the bottom (replacing
          the previous `justify-end` on the scroll parent). Using
          `justify-content: flex-end` together with `overflow-y: auto` makes
          the top of overflowing content render above scrollTop=0, where it's
          unreachable by the scrollbar — that was the "chat not scrollable"
          bug.
        */}
        <div className="mt-auto">
          {hasMore && (
            <div className="flex justify-center py-2">
              <button
                onClick={fetchOlder}
                className="text-[11px] text-[#697571] hover:text-[#374741] px-3 py-1 rounded-full bg-white border border-[#e6e8e7]"
              >
                Load older messages
              </button>
            </div>
          )}
          <ChatMessageList
            messages={messages}
            currentUserId={userId}
            isLoading={isLoading}
            onReply={startReply}
            onEdit={startEdit}
            onDelete={setPendingDelete}
            onJumpTo={jumpToMessage}
          />
        </div>
      </div>

      <div className="shrink-0 bg-white border-t border-[#e6e8e7]">
        <ActionForChat
          onSend={handleSend}
          onFileUpload={handleFileUpload}
          onVoiceNote={handleVoiceNote}
          isSending={
            sendMessage.isPending ||
            uploadFile.isPending ||
            editMessage.isPending
          }
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          editing={editing}
          onCancelEdit={() => setEditing(null)}
          onSaveEdit={handleSaveEdit}
          leadUsers={leadUsers}
        />
      </div>

      {/* Delete-for-everyone confirm (WhatsApp semantics) */}
      <ConfirmDialog
        open={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete for everyone?"
        tone="warning"
        warningTitle="This message will be deleted for everyone"
        warningDescription={`Participants will see "This message was deleted" instead.`}
        confirmLabel="Delete for everyone"
        loadingLabel="Deleting..."
        isLoading={deleteMessage.isPending}
      />
    </div>
  );
}
